/**
 * Checkbox Component
 * 
 * Accessible checkbox with label support.
 * Used for terms agreement and consent forms.
 */

import React, { forwardRef, InputHTMLAttributes } from 'react';
import { motion } from 'framer-motion';

// ============================================
// TYPES
// ============================================

interface CheckboxProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'type'> {
  /** Label text (can include JSX for links) */
  label?: React.ReactNode;
  /** Error state */
  error?: boolean;
  /** Error message */
  errorMessage?: string;
}

// ============================================
// COMPONENT
// ============================================

/**
 * Checkbox - Accessible checkbox input with label
 */
export const Checkbox = forwardRef<HTMLInputElement, CheckboxProps>(
  (
    {
      label,
      error = false,
      errorMessage,
      className = '',
      id,
      checked,
      onChange,
      ...props
    },
    ref
  ) => {
    // Generate unique ID if not provided
    const checkboxId = id || `checkbox-${Math.random().toString(36).substr(2, 9)}`;

    return (
      <div className="flex flex-col gap-2">
        <label
          htmlFor={checkboxId}
          className={`
            flex items-start gap-3 cursor-pointer group
            ${error ? 'text-red-600' : 'text-text-primary'}
            ${className}
          `}
        >
          {/* Custom checkbox container */}
          <div className="relative flex-shrink-0 mt-0.5">
            {/* Hidden native checkbox */}
            <input
              ref={ref}
              type="checkbox"
              id={checkboxId}
              checked={checked}
              onChange={onChange}
              className="sr-only"
              aria-invalid={error}
              aria-describedby={errorMessage ? `${checkboxId}-error` : undefined}
              {...props}
            />
            
            {/* Custom checkbox visual */}
            <div
              className={`
                w-5 h-5 rounded border-2 flex items-center justify-center
                transition-all duration-200
                ${checked 
                  ? 'bg-primary border-primary' 
                  : error 
                    ? 'bg-white border-red-500 group-hover:border-red-600'
                    : 'bg-white border-border group-hover:border-primary'
                }
                ${props.disabled ? 'opacity-50 cursor-not-allowed' : ''}
              `}
            >
              {/* Checkmark icon */}
              {checked && (
                <motion.svg
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{ type: 'spring', stiffness: 500, damping: 30 }}
                  className="w-3.5 h-3.5 text-white"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth={3}
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M5 13l4 4L19 7"
                  />
                </motion.svg>
              )}
            </div>
          </div>

          {/* Label text */}
          {label && (
            <span className="text-sm leading-relaxed select-none">
              {label}
            </span>
          )}
        </label>

        {/* Error message */}
        {error && errorMessage && (
          <motion.p
            id={`${checkboxId}-error`}
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            className="text-sm text-red-600 flex items-center gap-1 ml-8"
            role="alert"
          >
            <svg
              className="w-4 h-4 flex-shrink-0"
              fill="currentColor"
              viewBox="0 0 20 20"
            >
              <path
                fillRule="evenodd"
                d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
                clipRule="evenodd"
              />
            </svg>
            {errorMessage}
          </motion.p>
        )}
      </div>
    );
  }
);

Checkbox.displayName = 'Checkbox';

export default Checkbox;
